import React from "react";
import MarketTicker from "./MarketTicker";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  Wallet,
  LineChart,
  Upload,
  Play,
  LogOut,
  ListChecks,
  Briefcase,
  ChevronDown,
  Layers,
  UserCog,
  History,
  Bell,
  MoreHorizontal,
} from "lucide-react";

const NAV = {
  admin: {
    primary: [
      { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/clients", label: "Clients", icon: Users },
      { to: "/admin/portfolios", label: "Portfolios", icon: Briefcase },
      { to: "/admin/recommendations", label: "Recommendations", icon: ListChecks },
      { to: "/admin/send-alert", label: "Send Alert", icon: Bell },
    ],
    more: [
      { to: "/admin/engine-history", label: "Engine History", icon: History },
      { to: "/admin/indices", label: "Indices", icon: LineChart },
      { to: "/admin/notifications", label: "Alert History", icon: Bell },
      { to: "/admin/staff", label: "Staff", icon: UserCog },
      { to: "/admin/login-history", label: "Login History", icon: History },
    ],
  },
  crm: {
    primary: [
      { to: "/crm/clients", label: "Clients", icon: Users },
    ],
    more: [],
  },
  ra: {
    primary: [
      { to: "/ra/strategies", label: "Strategies", icon: Layers },
      { to: "/ra/recommendations", label: "Recommendations", icon: ListChecks },
      { to: "/ra/bhavcopy", label: "Bhavcopy", icon: Upload },
      { to: "/ra/engine", label: "Run Engine", icon: Play },
      { to: "/ra/backtest", label: "Backtest", icon: LineChart },
      { to: "/admin/engine-history", label: "Engine History", icon: History },
    ],
    more: [],
  },
  customer: {
    primary: [
      { to: "/portfolio", label: "Portfolio", icon: LineChart },
    ],
    more: [],
  },
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const cfg = NAV[user.role] || { primary: [], more: [] };
  const primaryItems = cfg.primary || [];
  const moreItems = cfg.more || [];
  const navItems = [...primaryItems, ...moreItems]; // mobile dropdown shows everything

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      {/* Live market ticker (above main header) */}
      <MarketTicker />
      <div className="bg-zinc-50 border-b border-zinc-200 px-4 py-1 text-[10.5px] text-zinc-500 text-center leading-relaxed">
        Stock prices sourced from Yahoo Finance · delayed ~15 min during market hours. AlphaCompounding.in is not responsible for decisions based on displayed prices. Verify on NSE before transacting.
      </div>
      {/* Top header */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-30 border-b border-zinc-200 bg-white/95 backdrop-blur"
      >
        <div className="flex items-center justify-between gap-6 px-6 lg:px-8 h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="h-7 w-7 rounded-md bg-zinc-950 text-white grid place-items-center font-display font-bold text-sm">
              A
            </span>
            <div className="leading-tight hidden sm:block">
              <div className="font-display font-bold text-zinc-950 text-sm">AlphaCompounding</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">PAS</div>
            </div>
          </Link>

          {/* Inline nav (hidden on small screens, dropdown takes over) */}
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {primaryItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors duration-150 ${
                      isActive
                        ? "bg-zinc-950 text-white"
                        : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
                    }`
                  }
                  end
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
            {moreItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="nav-more-trigger"
                    variant="ghost"
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950 h-auto"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    <span>More</span>
                    <ChevronDown className="h-3 w-3 text-zinc-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {moreItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.to} asChild>
                        <Link
                          to={item.to}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="cursor-pointer"
                        >
                          <Icon className="h-4 w-4 mr-2" /> {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          {/* User menu */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:inline-block text-[10px] px-2 py-1 rounded-sm bg-zinc-100 border border-zinc-200 text-zinc-600 font-medium uppercase tracking-wider">
              {user.role}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="user-menu-trigger"
                  variant="ghost"
                  className="h-9 px-2 gap-2 hover:bg-zinc-100"
                >
                  <span className="h-7 w-7 rounded-full bg-zinc-200 grid place-items-center text-zinc-700 text-xs font-semibold">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden md:inline text-sm text-zinc-800 font-medium max-w-[140px] truncate">
                    {user.name || user.email}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-zinc-500 font-normal uppercase tracking-wider">
                    Signed in as
                  </div>
                  <div className="text-sm text-zinc-900 truncate font-semibold mt-0.5">
                    {user.name || user.email}
                  </div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* Mobile-only: show nav inside menu */}
                <div className="md:hidden">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.to} asChild>
                        <Link to={item.to} className="cursor-pointer">
                          <Icon className="h-4 w-4 mr-2" /> {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                </div>
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={onLogout}
                  className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 px-6 lg:px-8 py-6 lg:py-8 max-w-[1600px] w-full mx-auto">
        {children}
      </main>

      {/* AlphaCompounding footer */}
      <footer className="border-t border-zinc-200 mt-6" data-testid="app-footer">
        <div className="max-w-[1600px] w-full mx-auto px-6 lg:px-8 py-4 text-center text-[11px] text-zinc-500 leading-relaxed">
          © AlphaCompounding.in · Portfolio Advisory Service
          <span className="mx-2 text-zinc-300">·</span>
          Stock prices sourced from Yahoo Finance · Delayed ~15 min during market hours
          <span className="mx-2 text-zinc-300">·</span>
          Verify on NSE India before transacting
        </div>
      </footer>
    </div>
  );
}
